import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, NO_ERRORS_SCHEMA, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, collection, getDocs, doc, getDoc, setDoc, query, where, updateDoc, deleteDoc } from '@angular/fire/firestore';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { Subscription, map, firstValueFrom, combineLatest } from 'rxjs';
import * as XLSX from 'xlsx';

import { ExcelUploaderComponent } from '../excel-uploader/excel-uploader.component';
import { UploadHistoryComponent } from '../excel-uploader/upload-history.component';
import { ExcelImportService } from '../services/excel-import.service';
import { ProfitReport } from '../models/report.model';
import { PdfGeneratorService } from '../services/pdf-generator.service';
import { FeeRateService, SmrsRate } from '../services/fee-rate.service';
import { StoreService } from '../services/store.service';
import { InvoiceService } from '../services/invoice.service';
import { ProfitReportService } from '../services/profit-report.service';
import { CompanyService } from '../services/company.service';
import { AccountService } from '../services/account.service';
import { ExchangeRateService } from '../services/exchange-rate.service';
import { SalesInvoiceService } from '../services/sales-invoice.service';
import { AgentProfitService } from '../services/agent-profit.service';
import { SalesInvoiceTemplateComponent } from '../invoice/sales-invoice/sales-invoice-template.component';
import { AccessService } from '../services/access.service';
import { EmailFormComponent } from '../email-form/email-form.component';
import { BrokerReportComponent } from './broker-report/broker-report.component';
import { OwnerService } from '../services/owner.service';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';

const LABEL_TOTAL_COMPANY_EXPENSE = 'Total Company Expense and Fee';
const LABEL_TOTAL_COMPANY_EXPENSE_USD = 'Total Company Expense and Fee (USD)';
const LABEL_TOTAL_SALES = 'Total Sales';
const LABEL_TOTAL_SALES_USD = 'Total Sales (USD)';
const LABEL_TOTAL_SMRS_FEES = 'Total SMRS Fees';
const LABEL_TOTAL_SMRS_FEES_USD = 'Total SMRS Fees (USD)';

@Component({
  selector: 'app-profit-report',
  standalone: true,
  imports: [CommonModule, FormsModule, ExcelUploaderComponent, UploadHistoryComponent, HttpClientModule, SalesInvoiceTemplateComponent, EmailFormComponent, BrokerReportComponent],
  providers: [ExchangeRateService],
  templateUrl: './profit-report.component.html',
  styleUrls: ['./profit-report.component.css'],
  schemas: [NO_ERRORS_SCHEMA]
})
export class ProfitReportComponent implements OnInit, OnDestroy {
  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private excelService = inject(ExcelImportService);
  private pdfService = inject(PdfGeneratorService);
  private feeRateService = inject(FeeRateService);
  private storeService = inject(StoreService);
  private invoiceService = inject(InvoiceService);
  private profitReportService = inject(ProfitReportService);
  private companyService = inject(CompanyService);
  private accountService = inject(AccountService);
  private exchangeRateService = inject(ExchangeRateService);
  private salesInvoiceService = inject(SalesInvoiceService);
  private agentProfitService = inject(AgentProfitService);
  private accessService = inject(AccessService);
  private cd = inject(ChangeDetectorRef);
  private http = inject(HttpClient);
  private ownerService = inject(OwnerService);
  private brandService = inject(BrandService);

  // Import Properties
  periods: string[] = [];
  selectedPeriod: string = '';
  isImporting: boolean = false;
  importProgress: number = 0;
  showUploader: boolean = true;
  importSuccessMessage: string = '';
  historyCollection: string = 'profit_report_upload_history';
  isFileProcessed: boolean = false;
  duplicateWarningData: any = null;
  validationErrors: string[] = [];
  validationWarnings: string[] = [];
  private subscriptions: Subscription[] = [];
  expenseCategories: string[] = [];
  rates: SmrsRate[] = [];
  private gridDataCache = new Map<string, any[]>();
  private fileProcessedCache = new Map<string, boolean>();
  private cachedStores: any[] = [];
  private cachedAggregations: any[] = [];
  private cachedProfitData: any[] = [];
  private reportDataCache = new Map<string, any>();
  private rawDataCache = new Map<string, { aggregations: any[], profitData: any[] }>();
  private exchangeRateCache = new Map<string, { rate: number, date?: Date }>();
  private exchangeRatePromiseCache = new Map<string, Promise<{ rate: number, date?: Date }>>();
  brands: Brand[] = [];

  // Company List & Modal Properties
  companies: any[] = [];
  companyGridData: any[] = [];
  filteredCompanyGridData: any[] = [];
  filterCompanyIds: Set<string> = new Set();
  showCompanyFilterDropdown = false;
  showCountryFilterDropdown = false;
  filterCountry: string = 'All';
  availableCountries: string[] = [];
  isLoadingGrid: boolean = false;
  private gridLoadSeq = 0;
  isCompanyListExpanded: boolean = true;
  expandedCompanyId: string | null = null;
  isReportModalOpen: boolean = false;
  selectedCompany: any = null;
  isLoadingReport: boolean = false;
  savingCompanyId: string | null = null;
  activeTab: 'pl-report' | 'import' = 'pl-report';
  reportType: 'standard' | 'sis' | 'broker' = 'standard';
  activePdfDropdown: string | null = null;
  activeEmailDropdown: string | null = null;

  // Email State
  isSavingPDFs = false;
  isEmailModalOpen = false;
  emailData: any = { to: '', subject: '', body: '', attachments: [] };
  currentEmailRow: any = null;
  
  isAddingAdjustment = false;
  newAdjustment: { description: string, amount: number | null } = { description: '', amount: null };
  
  // Dashboard Stats
  dashboardStats = {
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    totalFees: 0
  };

  owners: any[] = [];
  distributionOwners: any[] = [];
  selectedOwnerId: string | null = null;
  partnerDistributionTotal: number = 0;

  // Total P/L Report State
  isExportingTotalPL = false;
  totalPLData: any = null;
  
  historicalData: any[] = [];
  agentSystemInstruction = '';
  agentName = 'ERP Assistant';
  isAgentLoaded = false;

  // Static data matching your image
  // In a real scenario, this would be populated from the imported data
  reportData: ProfitReport & { 
    rawImportedData?: any[], 
    salesBreakdown?: any[],
    feeCollectionBreakdown?: any[],
    isInternational?: boolean,
    currency?: string,
    exchangeRate?: number,
    exchangeRateDate?: Date,
    totalSalesUSD?: number,
    totalFeesUSD?: number,
    totalStoreOperationFeeUSD?: number,
    totalNetProfitUSD?: number,
  shopNetProfitUSD?: number;
  brokerNetProfitUSD?: number;
  // New SIS-related fields
  sisCcRate?: number;
  sisProfitShare?: number;
  ccChargeAmount?: number;
  profitShareAmount?: number;
  reserveRate?: number;
  reserveAmount?: number;
  finalNetProfit?: number;
    brokerStoreDetails?: any;
    sisStoreDetails?: any[];
    salesInvoices?: any[]
  } = {
    companyName: 'VENTURE 2024, LLC',
    mallName: 'AMERICAN DREAM MALL',
    address: '1 American Dream Wy A227, East Rutherford, NJ 07073',
    period: 'Nov. 2025',
    date: new Date().toLocaleDateString('en-US'),
    salesData: {
      regular: 6704.00,
      licensed: 11125.00,
      special: 1626.00,
      idol: 1140.00
    },
    smrsFees: {
      rate8: 536.32,
      rate21: 2336.25,
      rate25: 406.50,
      rate32: 364.80
    },
    expenses: [],
    partners: [],
    salesBreakdown: [],
    salesInvoices: [],
    sisCcRate: 0,
    sisProfitShare: 0,
    ccChargeAmount: 0,
    profitShareAmount: 0,
    reserveRate: 0,
    reserveAmount: 0,
    finalNetProfit: 0,
  };

  ngOnInit() {
    this.generatePeriods();
    this.loadChartOfAccounts();
    this.loadAgentPrompt();
    
    // Fetch Companies and Brands with User Scope
    this.subscriptions.push(
      combineLatest([
        this.companyService.getCompanies().pipe(
          map(companies => companies.filter(c => (c as any)['isDeleted'] !== true))
        ),
        this.brandService.getBrands()
      ]).subscribe(async ([companies, brands]) => {
        this.brands = brands;

        // Apply Page Scope / Owner restrictions
        const user = this.auth.currentUser;
        if (user) {
          const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
          const scope = await this.accessService.getUserPageScope('profit-report', profile);
          if (scope.isRestricted) {
            const allowedCompanySet = new Set(scope.companyIds);
            this.companies = companies.filter(c => c.id && allowedCompanySet.has(c.id));
          } else {
            this.companies = companies;
          }
        } else {
          this.companies = companies;
        }
        
        await this.initCompanyFilters();
        this.gridDataCache.clear();
        this.rawDataCache.clear();
        this.reportDataCache.clear();
        if (this.selectedPeriod) this.loadGridData();
      })
    );

    this.subscriptions.push(
      this.excelService.isImporting$.subscribe(val => this.isImporting = val),
      this.excelService.importProgress$.subscribe(val => this.importProgress = val),
      this.excelService.currentImportPeriod$.subscribe(period => {
        if (period) {
          this.selectedPeriod = period;
          this.onPeriodChange(period);
        }
      })
    );

    this.subscriptions.push(
      this.feeRateService.getRates().subscribe(rates => {
        this.rates = rates;
        this.gridDataCache.clear();
        this.rawDataCache.clear();
        this.reportDataCache.clear();
        if (this.selectedPeriod) this.loadGridData();
      })
    );

    this.subscriptions.push(
      this.ownerService.getOwners().subscribe(async owners => {
        const user = this.auth.currentUser;
        let profile = null;
        let scope = null;
        if (user) {
          profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
          scope = await this.accessService.getUserPageScope('profit-report', profile);
        }

        if (scope && scope.isRestricted) {
          const assignedOwnerSet = new Set(scope.ownerIds);
          this.owners = owners.filter(o => o.id && assignedOwnerSet.has(o.id));
          this.distributionOwners = owners.filter(o => o.id && o.showTotalDistribution && assignedOwnerSet.has(o.id));
          // If only 1 assigned distribution owner, auto-select them
          if (this.distributionOwners.length === 1 && this.distributionOwners[0].id) {
            this.selectedOwnerId = this.distributionOwners[0].id;
          }
        } else {
          this.owners = owners;
          this.distributionOwners = owners.filter(o => o.showTotalDistribution);
        }

        if (owners.length > 0) {
          let savedOwnerId = null;
          if (user) {
            const prefs = await this.getUserPreferences(user.uid);
            savedOwnerId = prefs?.['selectedOwnerId'];
          }
          if (savedOwnerId && this.distributionOwners.some(o => o.id === savedOwnerId)) {
            this.selectedOwnerId = savedOwnerId;
          } else if (this.selectedOwnerId && !this.distributionOwners.some(o => o.id === this.selectedOwnerId)) {
            this.selectedOwnerId = this.distributionOwners.length > 0 ? this.distributionOwners[0].id : null;
          }
          this.calculatePartnerDistributionTotal();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async loadAgentPrompt() {
    try {
      // Replace 'PROFIT_REPORT_AGENT_ID' with the actual document ID from your database
      const agentConfig = await this.agentProfitService.getAgentConfig('PROFIT_REPORT_AGENT_ID');
      
      if (agentConfig && agentConfig.metaprompt) {
        this.agentSystemInstruction = agentConfig.metaprompt;
        this.agentName = agentConfig.name || 'ERP Assistant';
      } else {
        this.agentSystemInstruction = await firstValueFrom(
          this.http.get('assets/profit-agent-metaprompt.txt', { responseType: 'text' })
        );
      }
    } catch (err) {
      console.warn('Failed to load agent prompt. Using fallback.', err);
      try {
        if (!this.agentSystemInstruction) {
          this.agentSystemInstruction = await firstValueFrom(this.http.get('assets/profit-agent-metaprompt.txt', { responseType: 'text' }));
        }
      } catch (e) {}
    } finally {
      this.isAgentLoaded = true;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    if (this.isReportModalOpen) {
      this.closeReportModal();
    }
    if (this.showCompanyFilterDropdown) {
      this.showCompanyFilterDropdown = false;
    }
    if (this.showCountryFilterDropdown) {
      this.showCountryFilterDropdown = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.company-filter-dropdown')) {
      this.showCompanyFilterDropdown = false;
    }
    if (!target.closest('.country-filter-dropdown')) {
      this.showCountryFilterDropdown = false;
    }
    if (!target.closest('.pdf-dropdown-container')) {
      this.activePdfDropdown = null;
    }
    if (!target.closest('.email-dropdown-container')) {
      this.activeEmailDropdown = null;
    }
  }

  get activeTabCompanies() {
    if (this.activeTab === 'import') return [];
    return this.companies;
  }

  get activeFilterCompanyIdsCount() {
    return this.activeTabCompanies.filter(c => this.filterCompanyIds.has(c.id)).length;
  }

  get isAllActiveCompaniesSelected() {
    const active = this.activeTabCompanies;
    return active.length > 0 && active.every(c => this.filterCompanyIds.has(c.id));
  }

  get isKcegOwnedSelected() {
    const kceg = this.activeTabCompanies.filter(c => c.kcegOwned);
    const activeSelected = this.activeTabCompanies.filter(c => this.filterCompanyIds.has(c.id));
    return kceg.length > 0 && activeSelected.length === kceg.length && kceg.every(c => this.filterCompanyIds.has(c.id));
  }

  getBrandName(id: string): string {
    if (!id) return '';
    const brand = this.brands.find(b => b.id === id);
    if (brand) return brand.name;

    // Fallback: If id is a known source ID (e.g. 'T0RdUUPStz2b979i3K4A') or alias, resolve to existing brand
    const norm = id.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const isPhotoLavie = id === 'T0RdUUPStz2b979i3K4A' || norm.includes('photolavie') || norm.includes('lavie');
    const isLfc = norm === 'lfc' || norm === 'lk' || norm.includes('fourcuts') || norm.includes('4cuts');

    if (isPhotoLavie) {
      const plBrand = this.brands.find(b => {
        const bNorm = (b.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        return bNorm.includes('photolavie') || bNorm.includes('lavie');
      });
      return plBrand ? plBrand.name : 'K-Photo Lavie';
    }

    if (isLfc) {
      const lfcBrand = this.brands.find(b => {
        const bNorm = (b.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        return bNorm === 'lfc' || bNorm === 'lk' || bNorm.includes('fourcuts') || bNorm.includes('4cuts');
      });
      return lfcBrand ? lfcBrand.name : 'LFC';
    }

    return id;
  }

  setActiveTab(tab: 'pl-report' | 'import') {
    this.activeTab = tab;
    if (tab === 'import') {
      if (this.selectedPeriod === 'All Periods') {
        this.selectedPeriod = '';
        this.isFileProcessed = false;
        this.importSuccessMessage = '';
      }
    } else {
      if (this.selectedPeriod) {
        this.loadGridData();
      } else {
        this.applyFilters();
      }
    }
  }

  async initCompanyFilters() {
    const user = this.auth.currentUser;
    if (user) {
      const prefs = await this.getUserPreferences(user.uid);
      const savedIds = prefs?.['companyIds'] || null;
      if (savedIds && Array.isArray(savedIds)) {
        const validIds = savedIds.filter(id => this.companies.some(c => c.id === id));
        if (validIds.length > 0) {
           this.filterCompanyIds = new Set(validIds);
        } else {
           if (savedIds.length > 0) {
             this.selectAllCompanies();
           } else {
             this.filterCompanyIds = new Set();
           }
        }
      } else {
        this.selectAllCompanies();
      }
    } else {
      this.selectAllCompanies();
    }
    this.applyFilters();
  }

  async getUserPreferences(uid: string) {
    try {
      const prefDoc = await getDoc(doc(this.firestore, 'users', uid, 'preferences', 'profit_report'));
      if (prefDoc.exists()) {
        return prefDoc.data();
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  }

  async saveUserPreferences(uid: string, preferences: string[]) {
    try {
      await setDoc(doc(this.firestore, 'users', uid, 'preferences', 'profit_report'), { companyIds: preferences }, { merge: true });
    } catch (e) {
      console.error(e);
    }
  }

  async loadChartOfAccounts() {
    const accounts = await firstValueFrom(this.accountService.getAccounts());
    this.expenseCategories = accounts.map(a => a.name).sort();
  }

  generatePeriods() {
    const startDate = new Date(2024, 0, 1);
    const currentDate = new Date();
    const tempDate = new Date(startDate);

    while (tempDate <= currentDate) {
      const year = tempDate.getFullYear();
      const month = (tempDate.getMonth() + 1).toString().padStart(2, '0');
      this.periods.push(`${year}/${month}`);
      tempDate.setMonth(tempDate.getMonth() + 1);
    }
    this.periods.reverse();
  }

  async onPeriodChange(period: string) {
    this.selectedPeriod = period;
    this.importSuccessMessage = '';
    this.validationErrors = [];
    this.validationWarnings = [];
    this.isFileProcessed = false;
    
    if (period) {
      const snapshot = await this.profitReportService.getHistoryByPeriod(period);
      
      if (!snapshot.empty) {
        this.isFileProcessed = true;
        // TODO: Implement loadReportData(period) to aggregate 'profit_report_data'
      }
      this.loadGridData();
    }
  }

  // Helper to normalize company names for robust matching (removes spaces, punctuation, hidden chars)
  private normalizeCompanyName(name: string): string {
    return (name || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  async onFileSelected(file: File) {
    if (!this.selectedPeriod) {
      alert('Please select a period before uploading.');
      return;
    }

    this.isImporting = true;
    this.importProgress = 0;
    this.excelService.resetCancellation();
    this.duplicateWarningData = null;
    this.importSuccessMessage = '';
    this.validationErrors = [];
    this.validationWarnings = [];

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { cellDates: true });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const allRows = XLSX.utils.sheet_to_json(worksheet, { defval: '' }).map((row: any) => {
        const newRow: any = {};
        Object.keys(row).forEach(key => {
          // Replace zero-width/non-breaking spaces with normal spaces,
          // collapse multiple spaces into one, and trim the ends.
          const cleanKey = key.replace(/[\u200B-\u200D\uFEFF\xA0]/g, ' ')
                              .replace(/\s+/g, ' ')
                              .trim();
          newRow[cleanKey] = row[key];
        });
        return newRow;
      });

      if (allRows.length === 0) throw new Error('File is empty');

      // Filter rows to only include those matching the selected period
      const rawRows = allRows.filter((row: any) => {
        const pDateVal = row['Posting Date'];
        if (!pDateVal) return false;
        const pDate = new Date(pDateVal);
        if (isNaN(pDate.getTime())) return false;
        
        const year = pDate.getFullYear();
        const month = (pDate.getMonth() + 1).toString().padStart(2, '0');
        return `${year}/${month}` === this.selectedPeriod;
      });

      if (rawRows.length === 0) {
        throw new Error(`No transactions found for period ${this.selectedPeriod} in the uploaded file.`);
      }

      // Validate Companies against system records
      const systemCompanyMap = new Map<string, { name: string, id: string }>();
      this.companies.forEach(c => {
        const originalName = (c['company'] || '').toString();
        const normalized = this.normalizeCompanyName(originalName);
        if (normalized) systemCompanyMap.set(normalized, { name: originalName, id: c.id });
      });

      const invalidCompanies = new Set<string>();
      const validRows: any[] = [];

      rawRows.forEach((row: any) => {
        const fileCompanyName = (row['Company'] || '').toString();
        if (!fileCompanyName.trim()) return; // Skip empty rows

        const normalizedFile = this.normalizeCompanyName(fileCompanyName);
        
        if (systemCompanyMap.has(normalizedFile)) {
          // Auto-correct the company name in the row to match the system record exactly
          // Also attach the system ID for robust querying
          const match = systemCompanyMap.get(normalizedFile)!;
          row['Company'] = match.name;
          row['companyId'] = match.id;
          validRows.push(row);
        } else {
          invalidCompanies.add(fileCompanyName);
        }
      });

      if (invalidCompanies.size > 0) {
        if (validRows.length === 0) {
          this.validationErrors = Array.from(invalidCompanies);
        } else {
          this.validationWarnings = Array.from(invalidCompanies);
        }
      }

      if (validRows.length === 0) {
        this.isImporting = false;
        if (this.validationErrors.length === 0) {
          throw new Error('No valid rows found to import.');
        }
        return;
      }

      // Check duplicates
      const duplicateDoc = await this.profitReportService.getDuplicateHistory(this.selectedPeriod, file.name);

      if (duplicateDoc) {
        const duplicate = duplicateDoc.data();
        (duplicate as any).id = duplicateDoc.id;
        this.duplicateWarningData = { file, rows: validRows, existingRecord: duplicate };
        this.isImporting = false;
        return;
      }

      await this.executeImport(file, validRows);

    } catch (error: any) {
      console.error('Import failed:', error);
      alert(error.message || 'Import failed');
      this.isImporting = false;
    }
  }

  async proceedWithDuplicate() {
    if (!this.duplicateWarningData) return;
    this.isImporting = true;
    const { file, rows, existingRecord } = this.duplicateWarningData;
    this.duplicateWarningData = null;
    await this.executeImport(file, rows, existingRecord.importId, existingRecord.id);
  }

  cancelDuplicate() {
    this.duplicateWarningData = null;
    this.isImporting = false;
  }

  cancelImport() {
    if (confirm('Cancel import?')) {
      this.excelService.cancelImport();
      this.isImporting = false;
      this.showUploader = false;
      setTimeout(() => this.showUploader = true, 0);
    }
  }

  private async executeImport(file: File, rows: any[], previousImportId?: string, previousHistoryId?: string) {
    const user = this.auth.currentUser;
    const userName = user?.displayName || user?.email || 'Admin';
    const importId = this.profitReportService.createId();

    try {
      // Cleanup previous data if re-importing
      if (previousImportId) {
        await this.profitReportService.deleteImportData(previousImportId);
      }

      if (previousHistoryId) {
        await this.profitReportService.deleteHistory(previousHistoryId);
      }

      // Save Data Rows in batches (Firestore limit is 500)
      await this.profitReportService.saveProfitReportDataBatch(rows, importId, this.selectedPeriod, () => this.isImporting);
      
      this.gridDataCache.clear();
      this.rawDataCache.delete(this.selectedPeriod);
      this.reportDataCache.clear();
      this.fileProcessedCache.set(this.selectedPeriod, true);

      // // Generate and Save Summaries for the period
      // await this.generateAndSaveSummaries(this.selectedPeriod);

      // Upload raw file to Firebase Cloud Storage under Platform folder
      let downloadURL = '';
      try {
        downloadURL = await this.profitReportService.uploadRawImportFile(file, this.selectedPeriod);
      } catch (uploadErr) {
        console.warn('Failed to upload raw Excel file to Storage:', uploadErr);
      }

      // Save History
      await this.profitReportService.saveUploadHistory({
        fileName: file.name, period: this.selectedPeriod, importId,
        uploadedAt: new Date().toISOString(), uploadedBy: userName,
        downloadURL: downloadURL,
        status: 'Completed', recordCount: rows.length
      });

      this.importSuccessMessage = `Import completed. ${rows.length} rows processed.`;
      this.isFileProcessed = true;
      this.loadGridData();
    } finally {
      this.isImporting = false;
    }
  }

  // async generateAndSaveSummaries(period: string) {
  //   const rates = this.rates.filter(r => r.period === period);
  //   const stores = await firstValueFrom(this.storeService.getStores());
  //   const aggregations = await this.invoiceService.getAggregations(period);
  //   const profitData = await this.profitReportService.getProfitDataByPeriod(period);
    
  //   const ownershipsSnap = await getDocs(collection(this.firestore, 'company_ownerships'));
  //   const ownerships = ownershipsSnap.docs.map(d => d.data());
    
  //   const ownersSnap = await getDocs(collection(this.firestore, 'owners'));
  //   const ownersMap = new Map(ownersSnap.docs.map(d => [d.id, d.data()['name']]));

  //   const summaries = [];

  //   for (const company of this.companies) {
  //     let revenue = 0;
  //     let fees = 0;
      
  //     const companyStoreIds = new Set(stores
  //       .filter((s: any) => {
  //          const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
  //          return cids.includes(company.id);
  //       })
  //       .map((s: any) => String(s['storeId']).trim())
  //     );

  //     aggregations.forEach(agg => {
  //       if (companyStoreIds.has(String(agg['storeId']).trim()) && agg['revenues']) {
  //          rates.forEach(r => {
  //            let val = 0;
  //            r.importKeys?.forEach(k => val += (Number(agg['revenues'][k]) || 0));
  //            if (r.id && agg['revenues'][r.id]) val += (Number(agg['revenues'][r.id]) || 0);
  //            revenue += val;
  //            fees += val * (r.rate / 100);
  //          });
  //       }
  //     });

  //     let expenses = 0;
  //     profitData.forEach(p => {
  //       if (p['companyId'] === company.id) {
  //         expenses += (Number(p['amount']) || 0);
  //       }
  //     });

  //     const netProfit = revenue - (expenses + fees);

  //     const companyOwnerships = ownerships.filter(o => o['companyId'] === company.id);
  //     const partnerShares = companyOwnerships.map(o => {
  //       const pct = Number(o['profitSharePercent'] || o['percentage'] || 0);
  //       return {
  //         ownerId: o['ownerId'],
  //         name: ownersMap.get(o['ownerId']) || 'Unknown',
  //         percentage: pct,
  //         amount: netProfit * (pct / 100)
  //       };
  //     });

  //     summaries.push({
  //       companyId: company.id,
  //       companyName: company.company,
  //       period,
  //       revenue,
  //       expenses,
  //       fees,
  //       netProfit,
  //       partnerShares
  //     });
  //   }

  //   await this.profitReportService.savePeriodSummaries(summaries, period);
  // }

  async onDeleteHistory(period: string) {
    try {
      await this.profitReportService.deleteHistoryAndDataByPeriod(period);
      
      this.gridDataCache.clear();
      this.rawDataCache.delete(period);
      this.reportDataCache.clear();
      this.fileProcessedCache.set(period, false);
      
      if (this.selectedPeriod === period) {
        this.isFileProcessed = false;
        this.loadGridData();
      }
      
      this.importSuccessMessage = `Successfully deleted profit report data for ${period}`;
      setTimeout(() => this.importSuccessMessage = '', 3000);
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete profit report data. Please check console.');
    }
  }

  async onDeleteSingleFile(event: { historyId: string, importId: string, fileName: string, period: string, downloadURL?: string }) {
    try {
      await this.profitReportService.deleteSingleFileHistoryAndData(event.historyId, event.importId, event.downloadURL);
      
      this.gridDataCache.clear();
      this.rawDataCache.delete(event.period);
      this.reportDataCache.clear();

      // Check if there are remaining imports for this period
      const remainingHistorySnap = await this.profitReportService.getHistoryByPeriod(event.period);
      const hasRemaining = !remainingHistorySnap.empty;
      this.fileProcessedCache.set(event.period, hasRemaining);
      
      if (this.selectedPeriod === event.period) {
        this.isFileProcessed = hasRemaining;
        this.loadGridData();
      }
      
      this.importSuccessMessage = `Successfully deleted file "${event.fileName}" for ${event.period}`;
      setTimeout(() => this.importSuccessMessage = '', 3500);
    } catch (error) {
      console.error('Single file delete failed:', error);
      alert(`Failed to delete file "${event.fileName}". Please check console.`);
    }
  }

  async getExchangeRate(currency: string, period: string): Promise<{ rate: number, date?: Date }> {
    const key = `${currency}_${period}`;
    if (this.exchangeRateCache.has(key)) {
      return this.exchangeRateCache.get(key)!;
    }

    if (this.exchangeRatePromiseCache.has(key)) {
      return this.exchangeRatePromiseCache.get(key)!;
    }

    const promise = (async () => {
      const rateData = await this.invoiceService.getExchangeRate(currency, period);
      if (rateData !== null) {
        this.exchangeRateCache.set(key, rateData);
        this.exchangeRatePromiseCache.delete(key);
        return rateData;
      }

      try {
        // Fallback: Fetch latest rate from public API via service
        if (currency === 'CAD') {
          const apiData = await this.exchangeRateService.getCADtoUSDRate(period);
          if (apiData) {
            const data = { rate: apiData.rate, date: apiData.date };
            this.exchangeRateCache.set(key, data);
            this.exchangeRatePromiseCache.delete(key);
            return data;
          }
        }
      } catch (error) {
        console.warn('Error fetching exchange rate:', error);
      }
      const defaultData = { rate: 1 };
      this.exchangeRateCache.set(key, defaultData);
      this.exchangeRatePromiseCache.delete(key);
      return defaultData; // Default fallback
    })();

    this.exchangeRatePromiseCache.set(key, promise);
    return promise;
  }

  selectAllCompanies() {
    this.filterCompanyIds = new Set(this.companies.map(c => c.id));
  }

  toggleCompanyFilterDropdown() {
    this.showCompanyFilterDropdown = !this.showCompanyFilterDropdown;
  }

  toggleCountryFilterDropdown() {
    this.showCountryFilterDropdown = !this.showCountryFilterDropdown;
  }

  selectCountryFilter(country: string) {
    this.filterCountry = country;
    this.showCountryFilterDropdown = false;
    this.applyFilters();
  }

  async toggleCompanyFilterItem(companyId: string) {
    if (this.filterCompanyIds.has(companyId)) {
      this.filterCompanyIds.delete(companyId);
    } else {
      this.filterCompanyIds.add(companyId);
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async toggleAllCompanyFilter() {
    const active = this.activeTabCompanies;
    if (this.isAllActiveCompaniesSelected) {
      active.forEach(c => this.filterCompanyIds.delete(c.id));
    } else {
      active.forEach(c => this.filterCompanyIds.add(c.id));
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async toggleKcegOwnedFilter() {
    const kceg = this.activeTabCompanies.filter(c => c.kcegOwned);
    if (this.isKcegOwnedSelected) {
      kceg.forEach(c => this.filterCompanyIds.delete(c.id));
    } else {
      this.activeTabCompanies.forEach(c => this.filterCompanyIds.delete(c.id));
      kceg.forEach(c => this.filterCompanyIds.add(c.id));
    }
    this.applyFilters();
    await this.saveCompanyPreferences();
  }

  async saveCompanyPreferences() {
    const user = this.auth.currentUser;
    if (user) {
      await this.saveUserPreferences(user.uid, Array.from(this.filterCompanyIds));
    }
  }

  getSelectedOwnerName(): string {
    const owner = this.distributionOwners.find(o => o.id === this.selectedOwnerId);
    return owner ? owner.name : '';
  }

  applyFilters() {
    this.filteredCompanyGridData = this.companyGridData.filter(row => {
      const countryMatch = this.filterCountry === 'All' || row.country === this.filterCountry;
      const idMatch = this.filterCompanyIds.has(row.id);
      return countryMatch && idMatch;
    });
    this.calculateDashboardStats();
  }

  async calculatePartnerDistributionTotal() {
    this.partnerDistributionTotal = 0;
    
    if (!this.selectedOwnerId || !this.filteredCompanyGridData || this.filteredCompanyGridData.length === 0) {
      this.cd.detectChanges();
      return;
    }

    const promises = this.filteredCompanyGridData.map(async (row) => {
      const netIncome = row.finalNetProfit !== undefined ? row.finalNetProfit : row.netProfit;
      
      if (row.id) {
        const distributions = await this.profitReportService.calculatePartnerDistribution(
          row.id, 
          netIncome, 
          this.selectedPeriod, 
          this.selectedOwnerId!
        );
        return distributions.reduce((sum: number, dist: any) => sum + (dist.amount || 0), 0);
      }
      return 0;
    });

    const results = await Promise.all(promises);
    this.partnerDistributionTotal = results.reduce((sum, val) => sum + val, 0);
    this.cd.detectChanges();
  }

  async onPartnerSelectionChange() {
    this.calculatePartnerDistributionTotal();
    const user = this.auth.currentUser;
    if (user) {
      try {
        await setDoc(doc(this.firestore, 'users', user.uid, 'preferences', 'profit_report'), { selectedOwnerId: this.selectedOwnerId }, { merge: true });
      } catch (e) {
        console.error('Failed to save partner preference', e);
      }
    }
  }

  calculateDashboardStats() {
    let revenue = 0;
    let expenses = 0;
    let profit = 0;
    let fees = 0;

    this.filteredCompanyGridData.forEach(row => {
      revenue += (row.totalRevenue || 0);
      expenses += (row.totalStoreOperationFeeUSD || 0);
      profit += (row.netProfit || 0);
      fees += (row.totalFees || 0);
    });

    this.dashboardStats = {
      totalRevenue: revenue,
      totalExpenses: expenses,
      totalFees: fees,
      netProfit: profit
    };
    
    this.calculatePartnerDistributionTotal();
  }

  async loadGridData() {
    // Company, rate and period changes can each start a load; only the newest load may publish rows (no stale/cross-period data).
    const loadSeq = ++this.gridLoadSeq;
    if (!this.selectedPeriod || this.activeTab === 'import') {
      this.companyGridData = [];
      this.filteredCompanyGridData = [];
      return;
    }

    const cacheKey = `${this.selectedPeriod}_${this.activeTab}`;

    if (this.gridDataCache.has(cacheKey)) {
      this.companyGridData = this.gridDataCache.get(cacheKey)!;
      if (this.rawDataCache.has(this.selectedPeriod)) {
        const raw = this.rawDataCache.get(this.selectedPeriod)!;
        this.cachedAggregations = raw.aggregations;
        this.cachedProfitData = raw.profitData;
      }
      const countries = new Set<string>();
      this.companyGridData.forEach(r => {
        if (r.country) countries.add(r.country);
      });
      this.availableCountries = Array.from(countries).sort();
      this.applyFilters();
      return;
    }

    this.isLoadingGrid = true;

    try {
      if (this.expenseCategories.length === 0) {
        await this.loadChartOfAccounts();
      }

      const periodRates = this.rates.filter(r => r.period === this.selectedPeriod);
      const validCategories = new Set(this.expenseCategories.map(c => c.toLowerCase()));

      let stores = this.cachedStores;
      if (stores.length === 0) {
        const storesSnap = await this.storeService.getStoresSnapshot();
        stores = storesSnap.docs.map(d => d.data());
        this.cachedStores = stores;
      }

      // Fetch email statuses
      const statusQuery = query(collection(this.firestore, 'profit_report_status'), where('period', '==', this.selectedPeriod));
      const statusDocs = await getDocs(statusQuery);
      const statusMap = new Map<string, string>();
      const reportAdjustmentsMap = new Map<string, any[]>();
      statusDocs.forEach(d => {
        statusMap.set(d.data()['companyId'], d.data()['emailStatus']);
        if (d.data()['adjustments']) {
          reportAdjustmentsMap.set(d.data()['companyId'], d.data()['adjustments']);
        }
      });

      // Fetch Aggregations
      const aggDocs = await this.invoiceService.getAggregations(this.selectedPeriod);
      this.cachedAggregations = aggDocs;

      // Fetch Expenses
      const expenseQuery = query(collection(this.firestore, 'profit_report_data'), where('period', '==', this.selectedPeriod));
      const expenseSnap = await getDocs(expenseQuery);
      const expenseData = expenseSnap.docs.map(d => d.data());
      this.cachedProfitData = expenseData;

      // Fetch all invoices once
      let allSalesInvoices: any[] = [];
      try {
        const invoicesSnap = await getDocs(collection(this.firestore, 'sales_invoices'));
        allSalesInvoices = invoicesSnap.docs.map(d => d.data());
      } catch (e) {
        console.warn('Failed to bulk fetch sales invoices', e);
      }

      let invoiceStatusMap = new Map<string, string>();
      try {
        const invoicesDocs = await this.invoiceService.getInvoices(this.selectedPeriod);
        invoicesDocs.forEach((inv: any) => {
            invoiceStatusMap.set(inv.companyId, inv.status || 'Not Paid');
        });
      } catch (e) { }

      const gridRows = await Promise.all(this.companies.map(async c => {
        const mappedBrand = this.getBrandName(c.brand || '');
        const currentRates = periodRates.filter(r => {
          const rBrand = this.getBrandName((r as any).brand || '');
          return rBrand === mappedBrand;
        });

        const reportAdjustments = reportAdjustmentsMap.get(c.id) || [];
        const financials = await this.calculateCompanyFinancials(c, this.selectedPeriod, stores, aggDocs, expenseData, allSalesInvoices, reportAdjustments, currentRates, validCategories, undefined, invoiceStatusMap);

        // Calculate detailed store breakdown for the expanded sub-table
        const storeDetails: any[] = [];
        const storeTotals = {
          revenues: {} as Record<string, number>,
          fees: {} as Record<string, number>,
          totalRevenue: 0,
          totalFee: 0
        };

        currentRates.forEach(r => {
          storeTotals.revenues[r.name] = 0;
          storeTotals.fees[r.name] = 0;
        });

        financials.companyStores.forEach((store: any) => {
          const sId = String(store.storeId).trim();
          const storeAggs = aggDocs.filter(d => String(d['storeId']).trim() === sId);
          
          let sTotalRev = 0;
          let sTotalFee = 0;
          const sRevs: Record<string, number> = {};
          const sFees: Record<string, number> = {};

          currentRates.forEach(r => {
            sRevs[r.name] = 0;
            sFees[r.name] = 0;
          });

          storeAggs.forEach(agg => {
            if (agg.revenues) {
              currentRates.forEach(r => {
                const val = this.invoiceService.calculateRevenueForRate(agg, r);
                    const fee = c.isFeeCollection ? 0 : val * (r.rate / 100);
                sRevs[r.name] += val;
                sFees[r.name] += fee;
                sTotalRev += val;
                sTotalFee += fee;

                storeTotals.revenues[r.name] += val;
                storeTotals.fees[r.name] += fee;
              });
            }
          });

          if (sTotalRev > 0 || sTotalFee > 0) {
            storeTotals.totalRevenue += sTotalRev;
            storeTotals.totalFee += sTotalFee;

            storeDetails.push({
              storeId: sId,
              revenues: sRevs,
              totalRevenue: sTotalRev,
              fees: sFees,
              totalFee: sTotalFee
            });
          }
        });

        storeDetails.sort((a, b) => a.storeId.localeCompare(b.storeId));

        return {
          id: c.id,
          companyName: c.company,
          brand: mappedBrand,
          isKcegOwned: !!c.kcegOwned,
          period: this.selectedPeriod,
          totalRevenue: financials.totalSalesAmount,
          totalFees: Math.abs(financials.smrsFeeAmount),
          totalOpsAndFees: financials.totalOpsAndFees,
          totalStoreOperationFeeUSD: financials.totalStoreOperationFeeUSD,
          netProfit: financials.netProfit,
          finalNetProfit: financials.finalNetProfit,
          shopNetProfit: financials.shopNetProfit,
          brokerNetProfit: financials.brokerNetProfit,
          reportType: financials.reportType,
          rawCompany: c,
          currency: financials.currencyCode,
          exchangeRate: financials.exchangeRate,
          country: financials.country,
          emailStatus: statusMap.get(c.id) || 'Not Sent',
          rates: currentRates.map(r => ({ name: r.name, rate: r.rate })),
          storeDetails: storeDetails,
          storeTotals: storeTotals,
          feeCollectionBreakdown: financials.feeCollectionBreakdown,
          totalFeeCollectionAmount: financials.totalSales,
          totalFeeCollectionLfcFee: financials.feeCollectionBreakdown ? financials.feeCollectionBreakdown.reduce((sum: number, item: any) => sum + (item.lfcFee || 0), 0) : 0,
          totalFeeCollectionKcegFee: financials.feeCollectionBreakdown ? financials.feeCollectionBreakdown.reduce((sum: number, item: any) => sum + (item.kcegFee || 0), 0) : 0,
          isInternational: financials.isInternational
        };
      }));

      if (loadSeq !== this.gridLoadSeq) return;
      this.companyGridData = gridRows;
      this.companyGridData.sort((a, b) => {
        if (a.rawCompany?.isFeeCollection && !b.rawCompany?.isFeeCollection) return -1;
        if (!a.rawCompany?.isFeeCollection && b.rawCompany?.isFeeCollection) return 1;
        return (a.companyName || '').localeCompare(b.companyName || '');
      });

      this.gridDataCache.set(cacheKey, this.companyGridData);
      this.rawDataCache.set(this.selectedPeriod, { aggregations: aggDocs, profitData: expenseData });

      const countries = new Set<string>();
      this.companyGridData.forEach(r => {
        if (r.country) countries.add(r.country);
      });
      this.availableCountries = Array.from(countries).sort();

      this.applyFilters();
      await this.loadHistoricalDataForAgent();
      this.historicalData.sort((a, b) => a.period.localeCompare(b.period));
    } catch (error) {
      console.error('Error loading grid data:', error);
    } finally {
      if (loadSeq === this.gridLoadSeq) this.isLoadingGrid = false;
    }
  }

  async loadHistoricalDataForAgent() {
    if (!this.selectedPeriod) return;

    if (this.expenseCategories.length === 0) {
      await this.loadChartOfAccounts();
    }
    const validCategories = new Set(this.expenseCategories.map(c => c.toLowerCase()));

    this.historicalData = [];

    // For agent context, we want to fetch data for the selected period and the 2 preceding periods to provide recent historical context. This allows the agent to analyze trends without overwhelming it with too much data.
    const periodsToFetch = [this.selectedPeriod, ...this.getPreviousPeriods(this.selectedPeriod, 2)];
    let allSalesInvoices: any[] = [];
    try {
      const invoicesSnap = await getDocs(collection(this.firestore, 'sales_invoices'));
      allSalesInvoices = invoicesSnap.docs.map(d => d.data());
    } catch (e) {
      console.warn('Failed to bulk fetch sales invoices for agent context', e);
    }

    // Bulk fetch ownerships and owners to prevent N+1 queries during agent context load
    let ownerships: any[] = [];
    let ownersMap = new Map<string, string>();
    try {
      const ownershipsSnap = await getDocs(collection(this.firestore, 'company_ownerships'));
      ownerships = ownershipsSnap.docs.map(d => d.data());
      
      const ownersSnap = await getDocs(collection(this.firestore, 'owners'));
      ownersMap = new Map(ownersSnap.docs.map(d => [d.id, d.data()['name']]));
    } catch (e) {
      console.warn('Failed to fetch ownerships/owners in bulk', e);
    }

    for (const period of periodsToFetch) {
      try {
        const profitQuery = query(collection(this.firestore, 'profit_report_data'), where('period', '==', period));
        const profitSnap = await getDocs(profitQuery);
        const profitData = profitSnap.docs.map(d => d.data());
        
        const statusQuery = query(collection(this.firestore, 'profit_report_status'), where('period', '==', period));
        const statusDocs = await getDocs(statusQuery);
        const reportAdjustmentsMap = new Map<string, any[]>();
        statusDocs.forEach(d => {
          if (d.data()['adjustments']) {
            reportAdjustmentsMap.set(d.data()['companyId'], d.data()['adjustments']);
          }
        });

        let stores = this.cachedStores;
        if (stores.length === 0) {
          const storesSnap = await this.storeService.getStoresSnapshot();
          stores = storesSnap.docs.map(d => d.data());
          this.cachedStores = stores;
        }

        const aggDocs = await this.invoiceService.getAggregations(period);
        const currentRates = this.rates.filter((r: any) => r.period === period);
        
        let invoiceStatusMap = new Map<string, string>();
        try {
          const invoicesDocs = await this.invoiceService.getInvoices(period);
          invoicesDocs.forEach((inv: any) => {
              invoiceStatusMap.set(inv.companyId, inv.status || 'Not Paid');
          });
        } catch (e) { }

        const periodDataByCompany = [];

        for (const c of this.companies) {
          const reportAdjustments = reportAdjustmentsMap.get(c.id) || [];
          
          const applicableReportTypes: ('standard' | 'sis' | 'broker')[] = ['standard'];
          if (c.sis) applicableReportTypes.push('sis');
          if (c.brokerReport) applicableReportTypes.push('broker');

          for (const rType of applicableReportTypes) {
            const financials = await this.calculateCompanyFinancials(c, period, stores, aggDocs, profitData, allSalesInvoices, reportAdjustments, currentRates, validCategories, rType, invoiceStatusMap);

            if (financials.totalSalesAmount === 0 && financials.totalFees === 0 && financials.totalCompanyExpense === 0) continue;

            const mappedBrand = this.getBrandName(c.brand || '');
            
            // Local calculation to bypass N+1 queries
            const companyOwnerships = ownerships.filter(o => o['companyId'] === c.id);
            const partners = companyOwnerships.map(o => {
              const pct = Number(o['profitSharePercent'] || o['percentage'] || 0);
              return {
                name: ownersMap.get(o['ownerId']) || 'Unknown',
                percentage: pct,
                amount: (financials.finalNetProfit || 0) * (pct / 100)
              };
            });
            const partnersString = partners.length > 0 ? partners.map((p: any) => `${p.name}(${p.percentage}%):${p.amount}`).join(';') : '';
            
            let storeDetailsStr = '';
            financials.companyStores.forEach((store: any) => {
              const sId = String(store.storeId).trim();
              const storeAggs = aggDocs.filter(d => String(d['storeId']).trim() === sId);
              let sTotalRev = 0;
              let sTotalFee = 0;
              storeAggs.forEach(agg => {
                if (agg.revenues) {
                  currentRates.forEach(r => {
                    const val = this.invoiceService.calculateRevenueForRate(agg, r);
                  const fee = c.isFeeCollection ? 0 : val * (r.rate / 100);
                    sTotalRev += val;
                    sTotalFee += fee;
                  });
                }
              });
              if (financials.isInternational) {
                sTotalRev *= financials.exchangeRate;
                sTotalFee *= financials.exchangeRate;
              }
              if (sTotalRev > 0 || Math.abs(sTotalFee) > 0) {
                 storeDetailsStr += `${store.name || store.storeId}(Rev:${sTotalRev.toFixed(2)},Fee:${Math.abs(sTotalFee).toFixed(2)});`;
              }
            });

            let feeCollectionBreakdownStr = '';
            if (c.isFeeCollection && financials.feeCollectionBreakdown && financials.feeCollectionBreakdown.length > 0) {
              const lkLabel = mappedBrand === 'K-Photo Lavie' ? 'Commission' : 'LK Fee';
              feeCollectionBreakdownStr = financials.feeCollectionBreakdown.map((m: any) => `${m.companyName}(${m.status}):${m.amount}(${lkLabel}:${m.lfcFee||0},KCEG:${m.kcegFee||0})`).join('; ');
            }

            periodDataByCompany.push({
              companyName: c.company,
              brand: mappedBrand,
              isKcegOwned: !!c.kcegOwned,
              reportType: financials.reportType,
              totalRevenue: financials.totalSalesAmount,
              totalFees: Math.abs(financials.smrsFeeAmount),
              totalExpenses: financials.isInternational ? Math.abs(financials.totalCompanyExpense * financials.exchangeRate) : Math.abs(financials.totalCompanyExpense),
              expensesByCategory: financials.expensesByCategory,
              salesTaxAmount: Math.abs(financials.salesTaxAmount),
              salesTaxRate: financials.salesTaxRate,
              invoiceTotalDue: financials.isInternational ? financials.invoiceTotalDue * financials.exchangeRate : financials.invoiceTotalDue,
              totalAdjustments: financials.isInternational ? financials.totalAdjustments * financials.exchangeRate : financials.totalAdjustments,
              ccChargeAmount: financials.ccChargeAmount,
              profitShareAmount: financials.profitShareAmount,
              totalOpsAndFees: financials.totalOpsAndFees,
              netProfit: financials.netProfit,
              shopNetProfit: financials.shopNetProfit,
              brokerNetProfit: financials.brokerNetProfit,
              partners: partnersString,
              storeDetails: storeDetailsStr,
              feeCollectionBreakdown: feeCollectionBreakdownStr
            });
          }
        }


        this.historicalData.push({
          period,
          companies: periodDataByCompany
        });
      } catch (error) {
        console.warn(`Failed to fetch historical data for ${period}`, error);
      }
    }
  }

  getPreviousPeriods(period: string, count: number): string[] {
    const [yearStr, monthStr] = period.split('/');
    let year = parseInt(yearStr, 10);
    let month = parseInt(monthStr, 10);
    const periods = [];
    for (let i = 0; i < count; i++) {
      month -= 1;
      if (month === 0) {
        month = 12;
        year -= 1;
      }
      periods.push(`${year}/${month.toString().padStart(2, '0')}`);
    }
    return periods;
  }

  toggleCompanyList() {
    this.isCompanyListExpanded = !this.isCompanyListExpanded;
  }

  toggleCompanyExpansion(company: any) {
    this.expandedCompanyId = this.expandedCompanyId === company.id ? null : company.id;
  }

  togglePdfDropdown(rowId: string, event: Event) {
    event.stopPropagation();
    if (this.activePdfDropdown === rowId) {
      this.activePdfDropdown = null;
    } else {
      this.activePdfDropdown = rowId;
      this.activeEmailDropdown = null;
    }
  }

  toggleEmailDropdown(rowId: string, event: Event) {
    event.stopPropagation();
    if (this.activeEmailDropdown === rowId) {
      this.activeEmailDropdown = null;
    } else {
      this.activeEmailDropdown = rowId;
      this.activePdfDropdown = null;
    }
  }

  async viewReport(company: any) {
    this.selectedCompany = company;
    this.reportType = 'standard';
    this.reportData.companyName = company.company;
    this.reportData.address = company.address || '';
    this.isReportModalOpen = true;
    await this.generateReportData(company);
  }

  // New method to handle report type change
  async onReportTypeChange() {
    if (!this.selectedCompany) {
      return;
    }
    this.reportData.companyName = (this.reportType === 'broker' && this.selectedCompany.brokerName) ? this.selectedCompany.brokerName : this.selectedCompany.company;
    this.reportData.address = (this.reportType === 'broker' && this.selectedCompany.brokerLocation) ? this.selectedCompany.brokerLocation : (this.selectedCompany.address || '');
    this.reportDataCache.delete(`${this.selectedPeriod}_${this.selectedCompany.id}_${this.reportType}`); // Clear cache for new report type
    await this.generateReportData(this.selectedCompany);
  }

  async saveReportPdf(company: any, type: 'standard' | 'sis' | 'broker' = 'standard') {
    if (this.savingCompanyId) return;
    this.savingCompanyId = company.id;
    this.activePdfDropdown = null;
    
    try {
      this.reportType = type;
      this.reportData.companyName = (type === 'broker' && company.brokerName) ? company.brokerName : company.company;
      this.reportData.address = (type === 'broker' && company.brokerLocation) ? company.brokerLocation : (company.address || '');
      
      await this.generateReportData(company);
      
      await this.handlePdfAction(company, 'save');
    } catch (error) {
      console.error('Error saving PDF:', error);
      alert('Failed to save PDF.');
    } finally {
      this.savingCompanyId = null;
    }
  }

  async printReport() {
    if (!this.selectedCompany) return;
    await this.handlePdfAction(this.selectedCompany, 'open');
  }

  private async handlePdfAction(company: any, action: 'save' | 'open' | 'blob'): Promise<Blob | void> {
    let pdfWindow: Window | null = null;
    if (action === 'open') {
      // Open window immediately to avoid popup blockers
      pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
      }
    }

    this.cd.detectChanges();
    // Wait for view to update
    await new Promise(resolve => setTimeout(resolve, 1000));

    const content = document.getElementById('pdf-content');
    if (content) {
      const filename = `Profit_Report_${this.reportData.companyName.replace(/\s+/g, '_')}_${this.selectedPeriod.replace('/', '-')}.pdf`;
      const orientation = 'landscape';
      
      try {
        if (action === 'save') {
          await this.pdfService.savePdf(content, filename, orientation);
        } else if (action === 'open') {
          await this.pdfService.openPdf(content, filename, orientation, pdfWindow || undefined);
        } else if (action === 'blob') {
          return await this.pdfService.generateBlob(content, filename, orientation);
        }
      } catch (error) {
        if (pdfWindow) pdfWindow.close();
        console.error(error);
        if (action === 'blob') throw error;
      }
    } else {
      if (pdfWindow) pdfWindow.close();
      if (action === 'blob') throw new Error('PDF content not found');
    }
  }

  emailStoreReport(row: any, store: any) {
    // Placeholder for future store-specific email functionality
    alert(`Email functionality for an individual store (${store.storeId}) is not yet implemented.`);
  }

  async emailSingleReport(row: any, type: 'standard' | 'sis' | 'broker' = 'standard') {
    this.activeEmailDropdown = null;
    this.currentEmailRow = row;
    this.isSavingPDFs = true;
    try {
      this.reportType = type;
      this.reportData.companyName = (type === 'broker' && row.rawCompany.brokerName) ? row.rawCompany.brokerName : row.rawCompany.company;
      this.reportData.address = (type === 'broker' && row.rawCompany.brokerLocation) ? row.rawCompany.brokerLocation : (row.rawCompany.address || '');
      await this.generateReportData(row.rawCompany);

      const pdfBlob = await this.handlePdfAction(row.rawCompany, 'blob') as Blob;
      const user = this.auth.currentUser;
      
      const periodStr = this.selectedPeriod.replace(/\//g, '-');
      const companyNameForFile = this.reportData.companyName.replace(/\s+/g, '_');
      const fileRefName = `Profit_Report_${companyNameForFile}_${periodStr}`;
      
      const { downloadURL, storagePath } = await this.profitReportService.uploadTempPdf(user?.uid || 'temp', fileRefName, pdfBlob);

      let body = `Dear ${row.rawCompany.pocName || 'Partner'},\n\nPlease find attached the profit report for ${this.selectedPeriod}.\n\nBest regards,\nLFC Franchise Inc.`;

      if (user) {
        try {
          const template = await this.accessService.getEmailTemplate(user.uid, 'profit_report');
          if (template) body = template;
        } catch (e) {}
      }

      this.emailData = {
        to: row.rawCompany.email || '',
        subject: `Profit Report ${this.selectedPeriod} - ${this.reportData.companyName}`,
        body: body,
        attachments: [{
          name: `${fileRefName}.pdf`,
          url: downloadURL,
          storagePath: storagePath
        }]
      };
      this.isEmailModalOpen = true;
    } catch (error) {
      console.error('Error generating email attachment:', error);
      alert('Failed to generate report attachment.');
    } finally {
      this.isSavingPDFs = false;
    }
  }

  async onEmailSent(event?: any) {
    if (this.currentEmailRow) {
      const row = this.currentEmailRow;
      try {
        const docId = `${this.selectedPeriod.replace('/', '-')}_${row.id}`;
        await setDoc(doc(this.firestore, 'profit_report_status', docId), {
          period: this.selectedPeriod,
          companyId: row.id,
          emailStatus: 'Sent',
          emailSentAt: new Date().toISOString(),
          recipients: this.emailData.to || ''
        }, { merge: true });
        
        row.emailStatus = 'Sent';
        this.cd.detectChanges();
      } catch (e) {
        console.error("Failed to update email status", e);
      }
    }
    this.closeEmailModal(false);
  }

  async closeEmailModal(shouldDelete: boolean = false) {
    if (shouldDelete && this.emailData.attachments && this.emailData.attachments.length > 0) {
      for (const attachment of this.emailData.attachments) {
        if (attachment.storagePath) {
          try {
            await this.profitReportService.deleteTempPdf(attachment.storagePath);
          } catch (e) {}
        }
      }
    }
    this.isEmailModalOpen = false;
    this.emailData = { to: '', subject: '', body: '', attachments: [] };
    this.currentEmailRow = null;
  }

  async generateReportData(company: any) {
    this.isLoadingReport = true;
    const cacheKey = `${this.selectedPeriod}_${company.id}_${this.reportType}`;

    if (this.reportDataCache.has(cacheKey)) {
      this.reportData = JSON.parse(JSON.stringify(this.reportDataCache.get(cacheKey)));
      this.isLoadingReport = false;
      return;
    }

    if (this.expenseCategories.length === 0) {
      await this.loadChartOfAccounts();
    }

    try {
      if (this.selectedPeriod) {
        const [year, month] = this.selectedPeriod.split('/');
        const dateObj = new Date(parseInt(year), parseInt(month) - 1, 1);
        const monthName = dateObj.toLocaleString('en-US', { month: 'short' });
        this.reportData.period = `${monthName}. ${year}`;
        this.reportData.date = new Date().toLocaleDateString('en-US');
      }

      const mappedBrand = this.getBrandName(company.brand || '');
      const currentRates = this.rates.filter((r: any) => {
        const rBrand = this.getBrandName((r as any).brand || '');
        return r.period === this.selectedPeriod && rBrand === mappedBrand;
      });

      if (this.cachedStores.length === 0) {
        const storesSnap = await this.storeService.getStoresSnapshot();
        this.cachedStores = storesSnap.docs.map(d => d.data());
      }

      const aggDocs = this.cachedAggregations;
      const profitData = this.cachedProfitData;

      let allSalesInvoices: any[] = [];
      try {
        allSalesInvoices = await this.salesInvoiceService.getSalesInvoices(company.id);
      } catch (e) { }

      let invoiceStatusMap = new Map<string, string>();
      try {
        const invoicesDocs = await this.invoiceService.getInvoices(this.selectedPeriod);
        invoicesDocs.forEach((inv: any) => {
            invoiceStatusMap.set(inv.companyId, inv.status || 'Not Paid');
        });
      } catch (e) { }

      const docId = `${this.selectedPeriod.replace(/\//g, '-')}_${company.id}`;
      const statusDoc = await getDoc(doc(this.firestore, 'profit_report_status', docId));
      let reportAdjustments: any[] = [];
      if (statusDoc.exists() && statusDoc.data()['adjustments']) {
        reportAdjustments = statusDoc.data()['adjustments'];
      }

      const validCategories = new Set(this.expenseCategories.map(c => c.toLowerCase()));

      const financials = await this.calculateCompanyFinancials(company, this.selectedPeriod, this.cachedStores, aggDocs, profitData, allSalesInvoices, reportAdjustments, currentRates, validCategories, this.reportType, invoiceStatusMap);

      this.reportData.isInternational = financials.isInternational;
      this.reportData.currency = financials.currencyCode;
      this.reportData.exchangeRate = financials.exchangeRate;
      this.reportData.exchangeRateDate = financials.exchangeRateDate;
      this.reportData.salesBreakdown = financials.salesBreakdown;
      this.reportData.sisCcRate = financials.activeCcRate;
      this.reportData.sisProfitShare = financials.activeProfitShare;
      this.reportData.ccChargeAmount = financials.ccChargeAmount;
      this.reportData.profitShareAmount = financials.profitShareAmount;
      this.reportData.totalSalesUSD = financials.totalSalesUSD;
      this.reportData.totalFeesUSD = financials.totalFeesUSD;
      this.reportData.totalStoreOperationFeeUSD = financials.totalStoreOperationFeeUSD;
      this.reportData.totalNetProfitUSD = financials.totalNetProfitUSD;
      this.reportData.shopNetProfitUSD = financials.shopNetProfitUSD;
      this.reportData.brokerNetProfitUSD = financials.brokerNetProfitUSD;
      this.reportData.reserveRate = financials.reserveRate;
      this.reportData.reserveAmount = financials.reserveAmount;
      this.reportData.finalNetProfit = financials.finalNetProfit;
      this.reportData.feeCollectionBreakdown = financials.feeCollectionBreakdown;

      // SIS/Broker Details
      if (financials.reportType === 'broker') {
        const brokerStoreDetails = { rates: currentRates.map(r => ({ name: r.name, rate: r.rate })), stores: [] as any[], totals: { revenues: {} as any, totalRevenue: 0, fees: {} as any, totalFee: 0 }};
        
        currentRates.forEach(r => {
          brokerStoreDetails.totals.revenues[r.name] = 0;
          brokerStoreDetails.totals.fees[r.name] = 0;
        });

        financials.companyStores.forEach((store: any) => {
          const sId = String(store.storeId).trim();
          const storeAggs = aggDocs.filter(d => String(d['storeId']).trim() === sId);
          
          let sTotalRev = 0;
          let sTotalFee = 0;
          const sRevs: any = {};
          const sFees: any = {};
          
          currentRates.forEach(r => {
            sRevs[r.name] = 0;
            sFees[r.name] = 0;
          });

          storeAggs.forEach(agg => {
            if (agg.revenues) {
              currentRates.forEach(r => {
                const val = this.invoiceService.calculateRevenueForRate(agg, r);
                const fee = company.isFeeCollection ? 0 : val * (r.rate / 100);
                
                sRevs[r.name] += val;
                sFees[r.name] += fee;
                sTotalRev += val;
                sTotalFee += fee;

                brokerStoreDetails.totals.revenues[r.name] += val;
                brokerStoreDetails.totals.fees[r.name] += fee;
              });
            }
          });

          if (sTotalRev > 0) {
            brokerStoreDetails.stores.push({
              storeId: sId,
              storeName: store.name,
              revenues: sRevs,
              totalRevenue: sTotalRev,
              fees: sFees,
              totalFee: sTotalFee
            });
            brokerStoreDetails.totals.totalRevenue += sTotalRev;
            brokerStoreDetails.totals.totalFee += sTotalFee;
          }
        });
        
        brokerStoreDetails.stores.sort((a, b) => (a.storeName || '').localeCompare(b.storeName || ''));
        this.reportData.brokerStoreDetails = brokerStoreDetails;
      }

      if (financials.reportType === 'sis' && financials.companyStores.length > 1) {
        const sisStoreDetails: any[] = [];
        financials.companyStores.forEach((store: any) => {
          const sId = String(store.storeId).trim();
          const storeAggs = aggDocs.filter(d => String(d['storeId']).trim() === sId);
          
          const storeOnlyResult = this.invoiceService.calculateCompanyRevenueBreakdown(storeAggs, [store], currentRates, company.id, company.isFeeCollection);
          const sTotalSales = storeOnlyResult.totalRevenue;
          const sTotalFees = storeOnlyResult.totalFee;

          if (sTotalSales > 0 || sTotalFees > 0) {
            const sTotalSalesAmount = financials.isInternational ? (sTotalSales * financials.exchangeRate) : sTotalSales;
            const sSmrsFeeAmount = financials.isInternational ? (sTotalFees * financials.exchangeRate) : sTotalFees;
            
            const sCcChargeAmount = sTotalSalesAmount * (financials.activeCcRate / 100);
            const baseForProfitShare = sTotalSalesAmount - sSmrsFeeAmount - sCcChargeAmount;
            const sProfitShareAmount = baseForProfitShare * (financials.activeProfitShare / 100);

            sisStoreDetails.push({
              storeId: sId,
              storeName: store.name,
              totalSales: sTotalSalesAmount,
              totalFees: sSmrsFeeAmount,
              ccChargeAmount: sCcChargeAmount,
              profitShareAmount: sProfitShareAmount,
              salesBreakdown: storeOnlyResult.breakdown.map((b: any) => ({
                ...b,
                revenue: financials.isInternational ? b.revenue * financials.exchangeRate : b.revenue,
                fee: financials.isInternational ? b.fee * financials.exchangeRate : b.fee
              }))
            });
          }
        });
        
        sisStoreDetails.sort((a, b) => a.storeId.localeCompare(b.storeId));
        this.reportData.sisStoreDetails = sisStoreDetails;
      } else {
        this.reportData.sisStoreDetails = undefined;
      }

      // Build invoices
      if (financials.matchedInvoices.length > 0) {
        const combinedInvoice = {
          ...financials.matchedInvoices[0],
          isCombined: true,
          invoiceNumber: financials.matchedInvoices.map((i: any) => i.invoiceNumber).join(', '),
          items: financials.matchedInvoices.reduce((acc: any[], i: any) => {
            const mappedItems = (i.items || []).map((item: any) => ({ ...item, invoiceDate: i.invoiceDate }));
            return acc.concat(mappedItems);
          }, []),
          totalDue: financials.invoiceTotalDue
        };
        this.reportData.salesInvoices = [combinedInvoice];
      } else {
        this.reportData.salesInvoices = [];
      }

      // Rebuild Expenses Array
      const newExpenses = [];
      
      // Header: Total Sales
      const totalSalesLabel = financials.isInternational ? (company.isFeeCollection ? 'Total Fees Collected (USD)' : LABEL_TOTAL_SALES_USD) : (company.isFeeCollection ? 'Total Fees Collected' : LABEL_TOTAL_SALES);
      newExpenses.push({ 
        label: totalSalesLabel, 
        amount: financials.totalSalesAmount !== 0 ? financials.totalSalesAmount : null, 
        isHeader: true, 
        category: 'income' as const
      });

      const processedKeys = new Set<string>();

      // Operating Expenses Items
      this.expenseCategories.forEach(label => {
        const lowerLabel = label.toLowerCase();
        
        // Ignore CC Charge and Profit Share from imported expenses for standard report calculation
        if (this.reportType === 'standard' && (lowerLabel.includes('cc charge') || lowerLabel.includes('profit share'))) {
          return;
        }

        // Find match (case-insensitive)
        let amount = 0;
        let matchedKey = '';
        for (const [key, val] of financials.expenseMap.entries()) {
          if (key.toLowerCase() === lowerLabel) {
            amount = val;
            matchedKey = key;
            break;
          }
        }
        
        if (matchedKey) processedKeys.add(matchedKey);

        newExpenses.push({ 
          label: label, 
          amount: amount !== 0 ? amount : null, 
          category: 'expense' as const
        });
      });


      newExpenses.push({ 
        label: 'Company Expense', 
        amount: financials.totalCompanyExpense, 
        isHeader: true, 
        category: 'expense' as const
      });
      
      newExpenses.push({ 
        label: 'Other Expenses', 
        amount: null, 
        hideAmount: true,
        isHeader: true, 
        category: 'expense' as const
      } as any);

      if (this.reportType === 'standard') {
        if (company.sis) {
          newExpenses.push({
            label: 'Shop Net Profit',
            amount: financials.shopNetProfit !== 0 ? -financials.shopNetProfit : null,
            category: 'fee' as const,
            isFullWidth: true
          });
        }
        if (company.brokerReport) {
          newExpenses.push({
            label: 'Broker Net Profit',
            amount: financials.brokerNetProfit !== 0 ? -financials.brokerNetProfit : null,
            category: 'fee' as const,
            isFullWidth: true
          });
        }
      }

      const smrsFeeLabel = financials.isInternational ? LABEL_TOTAL_SMRS_FEES_USD : LABEL_TOTAL_SMRS_FEES;
      newExpenses.push({ 
        label: smrsFeeLabel, 
        amount: financials.smrsFeeAmount !== 0 ? financials.smrsFeeAmount : null, 
        category: 'fee' as const,
        isFullWidth: true
      });
      
      if (company.isFeeCollection) {
        newExpenses.push({
          label: mappedBrand === 'K-Photo Lavie' ? 'Commission' : 'LK Fee',
          amount: financials.isInternational ? (-financials.totalLfcFee * financials.exchangeRate) : -financials.totalLfcFee,
          category: 'fee' as const,
          isFullWidth: true
        });
      }
      
      if (financials.salesTaxRate > 0) {
        newExpenses.push({
          label: `Sales Tax (${financials.salesTaxRate}%)`,
          amount: financials.salesTaxAmount,
          category: 'fee' as const,
          isFullWidth: true
        });
      }

      newExpenses.push({
        label: 'Total Operation Expense',
        amount: financials.invoiceTotalDue !== 0 ? financials.invoiceTotalDue : null,
        category: 'fee' as const,
        isFullWidth: true
      });

      if (company.adjustments && Array.isArray(company.adjustments)) {
        company.adjustments.forEach((adj: any, index: number) => {
          const adjAmount = Number(adj.amount) || 0;
          newExpenses.push({
            label: adj.description || 'Adjustment',
            amount: adjAmount !== 0 ? adjAmount : null,
            category: 'fee' as const,
            isFullWidth: true,
            isAdjustment: true,
            adjustmentType: 'company',
            adjustmentIndex: index
          });
        });
      }
      reportAdjustments.forEach((adj: any, index: number) => {
        const adjAmount = Number(adj.amount) || 0;
        newExpenses.push({
          label: adj.description || 'Adjustment',
          amount: adjAmount !== 0 ? adjAmount : null,
          category: 'fee' as const,
          isFullWidth: true,
          isAdjustment: true,
          adjustmentType: 'report',
          adjustmentIndex: index
        });
      });

      newExpenses.push({
        label: financials.isInternational ? LABEL_TOTAL_COMPANY_EXPENSE_USD : LABEL_TOTAL_COMPANY_EXPENSE,
        amount: financials.totalOpsAndFees !== 0 ? financials.totalOpsAndFees : null,
        isHeader: true,
        category: 'expense' as const
      });

      this.reportData.expenses = newExpenses;

      financials.rawImportedData.sort((a: any, b: any) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateA - dateB;
      });
      this.reportData.rawImportedData = financials.rawImportedData;

      this.reportData.partners = await this.profitReportService.calculatePartnerDistribution(company.id, financials.finalNetProfit || 0, this.selectedPeriod);

      this.reportDataCache.set(cacheKey, JSON.parse(JSON.stringify(this.reportData)));

    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report data.');
    } finally {
      this.isLoadingReport = false;
    }
  }

  async calculateCompanyFinancials(
    company: any,
    period: string,
    stores: any[],
    aggDocs: any[],
    profitData: any[],
    allSalesInvoices: any[],
    reportAdjustments: any[],
    currentRates: any[],
    validCategories: Set<string>,
    reportTypeOverride?: 'standard' | 'sis' | 'broker',
    invoiceStatusMap?: Map<string, string>
  ) {
    let reportType = reportTypeOverride || 'standard';

    const companyStores = stores.filter((s: any) => {
      if (s.isDeleted) return false;
      const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
      return cids.includes(company.id);
    });

    let isInternational = false;
    let currencyCode = 'USD';
    let exchangeRate = 1;
    let exchangeRateDate: Date | undefined;
    let country = '미국';

    for (const s of companyStores) {
      const sCountry = s['country'] || '미국';
      if (sCountry !== '미국') {
        country = sCountry;
        currencyCode = s['currency'] || (['Canada', '캐나다'].includes(country) ? 'CAD' : 'USD');
        if (currencyCode !== 'USD') {
          isInternational = true;
          break;
        }
      }
    }

    if (isInternational) {
      const rateData = await this.getExchangeRate(currencyCode, period);
      exchangeRate = rateData.rate;
      exchangeRateDate = rateData.date;
    }

    const { breakdown: salesBreakdown, totalRevenue: totalSales, totalFee: totalFees } = 
      this.invoiceService.calculateCompanyRevenueBreakdown(aggDocs, companyStores, currentRates, company.id, company.isFeeCollection);

    const sisCcRate = Number(company.sisCcRate) || 0;
    const sisProfitShare = Number(company.sisProfitShare) || 0;
    const brokerCcRate = Number(company.brokerCcRate) || 0;
    const brokerProfitShare = Number(company.brokerProfitShare) || 0;

    const activeCcRate = reportType === 'broker' ? brokerCcRate : (reportType === 'sis' ? sisCcRate : 0);
    const activeProfitShare = reportType === 'broker' ? brokerProfitShare : (reportType === 'sis' ? sisProfitShare : 0);

    const totalSalesAmount = isInternational ? (totalSales * exchangeRate) : totalSales;
    const smrsFeeAmount = isInternational ? (-totalFees * exchangeRate) : -totalFees;

    let ccChargeAmount = 0;
    let profitShareAmount = 0;
    let totalSisCharges = 0;

    if (reportType === 'sis' || reportType === 'broker') {
      ccChargeAmount = totalSalesAmount * (activeCcRate / 100);
    }

    const expenseMap = new Map<string, number>();
    const expensesByCategory: Record<string, number> = {};
    let totalCompanyExpense = 0;
    const rawImportedData: any[] = [];

    const companyProfitData = profitData.filter(d => d['companyId'] === company.id);

    companyProfitData.forEach(d => {
      if (d['postingDate']) {
        const pDate = d['postingDate'].toDate ? d['postingDate'].toDate() : new Date(d['postingDate']);
        if (!isNaN(pDate.getTime())) {
          const year = pDate.getFullYear();
          const month = (pDate.getMonth() + 1).toString().padStart(2, '0');
          if (`${year}/${month}` !== period) return;
        }
      }

      const cat = (d['category'] || '').trim();
      if (!cat || !validCategories.has(cat.toLowerCase())) return;
      
      const amt = Number(d['amount'] || 0);
      expenseMap.set(cat, (expenseMap.get(cat) || 0) + amt);
      expensesByCategory[cat] = (expensesByCategory[cat] || 0) + amt;
      totalCompanyExpense += amt;

      rawImportedData.push({
        date: d['postingDate'] && typeof d['postingDate'].toDate === 'function' ? d['postingDate'].toDate() : d['postingDate'],
        checkNumber: d['checkNumber'] || '',
        description: d['description'] || '',
        category: d['category'] || '',
        amount: amt
      });
    });

    const [pYear, pMonth] = period.split('/');
    const periodPrefix = `${pYear}-${pMonth}`;
    const matchedInvoices = allSalesInvoices.filter((inv: any) => 
      inv.companyId === company.id && inv.invoiceDate && inv.invoiceDate.startsWith(periodPrefix)
    );
    // show all positive amounts
    const invoiceTotalDue = -(matchedInvoices.reduce((sum: number, i: any) => sum + (i.totalDue || 0), 0));
    

    let totalAdjustments = 0;
    if (company.adjustments && Array.isArray(company.adjustments)) {
      company.adjustments.forEach((adj: any) => {
        totalAdjustments += (Number(adj.amount) || 0);
      });
    }
    reportAdjustments.forEach((adj: any) => {
      totalAdjustments += (Number(adj.amount) || 0);
    });

    const salesTaxRate = Number(company.salesTax) || 0;
    const salesTaxAmount = salesTaxRate > 0 ? -(totalSalesAmount * (salesTaxRate / 100)) : 0;

    if (reportType === 'sis' || reportType === 'broker') {
      const baseForProfitShare = totalSalesAmount + smrsFeeAmount - ccChargeAmount;
      profitShareAmount = baseForProfitShare * (activeProfitShare / 100);
      totalSisCharges = ccChargeAmount + profitShareAmount;
    }

    const sisCcChargeAmount = totalSalesAmount * (sisCcRate / 100);
    const shopNetProfit = (totalSalesAmount + smrsFeeAmount - sisCcChargeAmount) * (sisProfitShare / 100);

    const brokerCcChargeAmount = totalSalesAmount * (brokerCcRate / 100);
    const brokerNetProfit = (totalSalesAmount + smrsFeeAmount - brokerCcChargeAmount) * (brokerProfitShare / 100);

    let shopNetProfitDeduction = 0;
    let brokerNetProfitDeduction = 0;
    if (reportType === 'standard') {
      if (company.sis) shopNetProfitDeduction = -shopNetProfit;
      if (company.brokerReport) brokerNetProfitDeduction = -brokerNetProfit;
    }

    const feeCollectionBreakdown: any[] = [];
    let totalLfcFee = 0;
    let totalKcegFee = 0;
    if (company.isFeeCollection) {
      aggDocs.forEach(d => {
          if (d['companyId'] === company.id && d['sourceCompanyName']) {
              const cName = d['sourceCompanyName'];
              let amount = 0;
              currentRates.forEach(r => {
                  if (r.id) amount += this.invoiceService.calculateRevenueForRate(d, r);
              });

              const lfcFee = d['sourceLfcFee'] || 0;
              const kcegFee = d['sourceKcegFee'] || 0;

              if (amount !== 0) {
                  totalLfcFee += lfcFee;
                  totalKcegFee += kcegFee;
                  const date = d['updatedAt'] || null;
                  let status = 'Paid';
                  if (d['sourceCompanyId'] && invoiceStatusMap) {
                      status = invoiceStatusMap.get(d['sourceCompanyId']) || 'Paid';
                  }

                  const existing = feeCollectionBreakdown.find((x: any) => x.companyName === cName);
                  if (existing) {
                      existing.amount += amount;
                      existing.lfcFee += lfcFee;
                      existing.kcegFee += kcegFee;
                      if (date && (!existing.date || date > existing.date)) {
                          existing.date = date;
                      }
                      existing.status = status;
                  } else {
                      feeCollectionBreakdown.push({ companyName: cName, amount, lfcFee, kcegFee, date, status });
                  }
              }
          }
      });
    }
    
    let lfcFeeExpense = company.isFeeCollection ? -totalLfcFee : 0;

    let totalOpsAndFees = 0;
    let netProfit = 0;
    let totalSalesUSD = totalSales;
    let totalFeesUSD = totalFees;

    if (isInternational) {
      totalSalesUSD = totalSales * exchangeRate;
      totalFeesUSD = totalFees * exchangeRate;
      
      const totalCompanyExpenseUSD = totalCompanyExpense * exchangeRate;
      const totalAdjustmentsUSD = totalAdjustments * exchangeRate;
      const lfcFeeExpenseUSD = lfcFeeExpense * exchangeRate;
      
      const totalOpsAndFeesUSD_Signed = totalCompanyExpenseUSD + smrsFeeAmount + salesTaxAmount + totalAdjustmentsUSD + (invoiceTotalDue * exchangeRate) + shopNetProfitDeduction + brokerNetProfitDeduction + lfcFeeExpenseUSD;
      
      totalOpsAndFees = totalOpsAndFeesUSD_Signed;
      netProfit = totalSalesUSD + totalOpsAndFeesUSD_Signed;
    } else {
      totalOpsAndFees = totalCompanyExpense + smrsFeeAmount + salesTaxAmount + invoiceTotalDue + totalAdjustments + shopNetProfitDeduction + brokerNetProfitDeduction + lfcFeeExpense;
      netProfit = totalSalesAmount + totalOpsAndFees;
    }

    const totalStoreOperationFeeUSD = Math.abs(totalOpsAndFees);
    const totalNetProfitUSD = netProfit;
    const shopNetProfitUSD = shopNetProfit;
    const brokerNetProfitUSD = brokerNetProfit;

    let reserveRate = 0;
    let reserveAmount = 0;
    let finalNetProfit: number | undefined;

    if (reportType === 'standard') {
      reserveRate = Number(company.reserveRate) || 0;
      reserveAmount = netProfit > 0 ? netProfit * (reserveRate / 100) : 0;
      finalNetProfit = netProfit - reserveAmount;
    }

    return {
      reportType,
      isInternational,
      currencyCode,
      exchangeRate,
      exchangeRateDate,
      country,
      totalSales,
      totalSalesAmount,
      totalFees,
      smrsFeeAmount,
      salesBreakdown,
      activeCcRate,
      activeProfitShare,
      ccChargeAmount,
      profitShareAmount,
      totalSisCharges,
      totalCompanyExpense,
      expenseMap,
      expensesByCategory,
      rawImportedData,
      invoiceTotalDue,
      matchedInvoices,
      totalAdjustments,
      salesTaxRate,
      salesTaxAmount,
      totalOpsAndFees,
      netProfit,
      totalSalesUSD,
      totalFeesUSD,
      totalStoreOperationFeeUSD,
      totalNetProfitUSD,
      shopNetProfit,
      shopNetProfitUSD,
      brokerNetProfit,
      brokerNetProfitUSD,
      reserveRate,
      reserveAmount,
      finalNetProfit,
      companyStores,
      totalLfcFee,
      totalKcegFee,
      feeCollectionBreakdown: feeCollectionBreakdown.filter(x => Math.abs(x.amount) > 0.01)
    };
  }

  closeReportModal() {
    this.isReportModalOpen = false;
    this.selectedCompany = null;
    this.isAddingAdjustment = false;
    this.newAdjustment = { description: '', amount: null };
  }

  async saveCustomAdjustment() {
    if (!this.newAdjustment.description || this.newAdjustment.amount == null) return;
    if (!this.selectedCompany) return;

    this.isLoadingReport = true;
    try {
      const companyId = this.selectedCompany.id;
      const docId = `${this.selectedPeriod.replace(/\//g, '-')}_${companyId}`;
      const statusRef = doc(this.firestore, 'profit_report_status', docId);
      
      const newAdj = { description: this.newAdjustment.description, amount: this.newAdjustment.amount };
      
      const statusDoc = await getDoc(statusRef);
      let currentAdjustments: any[] = [];
      if (statusDoc.exists() && statusDoc.data()['adjustments']) {
        currentAdjustments = statusDoc.data()['adjustments'];
      }
      currentAdjustments.push(newAdj);
      
      await setDoc(statusRef, { 
        period: this.selectedPeriod,
        companyId: companyId,
        adjustments: currentAdjustments 
      }, { merge: true });
      
      this.isAddingAdjustment = false;
      this.newAdjustment = { description: '', amount: null };
      
      this.reportDataCache.delete(`${this.selectedPeriod}_${companyId}_standard`);
      this.reportDataCache.delete(`${this.selectedPeriod}_${companyId}_sis`);
      this.reportDataCache.delete(`${this.selectedPeriod}_${companyId}_broker`);
      
      this.gridDataCache.clear();
      await this.loadGridData();
      
      await this.generateReportData(this.selectedCompany);
    } catch (error) {
      console.error('Error saving adjustment:', error);
      alert('Failed to save adjustment.');
      this.isLoadingReport = false;
    }
  }

  async deleteCustomAdjustment(index: number, type: 'company' | 'report' = 'report') {
    if (!this.selectedCompany) return;
    if (!confirm('Are you sure you want to delete this adjustment?')) return;

    this.isLoadingReport = true;
    try {
      const companyId = this.selectedCompany.id;
      
      if (type === 'company') {
        if (!this.selectedCompany.adjustments) return;
        const companyRef = doc(this.firestore, 'company', companyId);
        
        let currentAdjustments = [...this.selectedCompany.adjustments];
        currentAdjustments.splice(index, 1);
        
        await updateDoc(companyRef, { adjustments: currentAdjustments });
        this.selectedCompany.adjustments = currentAdjustments;
        
        const compIndex = this.companies.findIndex(c => c.id === companyId);
        if (compIndex !== -1) {
          this.companies[compIndex].adjustments = currentAdjustments;
        }
      } else {
        const docId = `${this.selectedPeriod.replace(/\//g, '-')}_${companyId}`;
        const statusRef = doc(this.firestore, 'profit_report_status', docId);
        const statusDoc = await getDoc(statusRef);
        if (statusDoc.exists() && statusDoc.data()['adjustments']) {
           let currentAdjustments = [...statusDoc.data()['adjustments']];
           currentAdjustments.splice(index, 1);
           await setDoc(statusRef, { adjustments: currentAdjustments }, { merge: true });
        }
      }

      this.reportDataCache.delete(`${this.selectedPeriod}_${companyId}_standard`);
      this.reportDataCache.delete(`${this.selectedPeriod}_${companyId}_sis`);
      this.reportDataCache.delete(`${this.selectedPeriod}_${companyId}_broker`);
      
      this.gridDataCache.clear();
      await this.loadGridData();
      
      await this.generateReportData(this.selectedCompany);
    } catch (error) {
      console.error('Error deleting adjustment:', error);
      alert('Failed to delete adjustment.');
      this.isLoadingReport = false;
    }
  }

  async deleteTransactionItem(item: any) {
    if (!this.selectedCompany) return;
    if (!confirm('Are you sure you want to delete this transaction? This will recalculate the profit report.')) return;

    this.isLoadingReport = true;
    try {
      const q = query(
        collection(this.firestore, 'profit_report_data'),
        where('companyId', '==', this.selectedCompany.id),
        where('period', '==', this.selectedPeriod),
        where('description', '==', item.description || ''),
        where('amount', '==', item.amount || 0),
        where('category', '==', item.category || '')
      );
      
      const snap = await getDocs(q);
      if (!snap.empty) {
        await deleteDoc(doc(this.firestore, 'profit_report_data', snap.docs[0].id));
        
        this.gridDataCache.clear();
        this.rawDataCache.delete(this.selectedPeriod);
        this.reportDataCache.delete(`${this.selectedPeriod}_${this.selectedCompany.id}_standard`);
        this.reportDataCache.delete(`${this.selectedPeriod}_${this.selectedCompany.id}_sis`);
        this.reportDataCache.delete(`${this.selectedPeriod}_${this.selectedCompany.id}_broker`);
        
        await this.loadGridData();
        await this.generateReportData(this.selectedCompany);
      } else {
        alert('Transaction not found in the database.');
        this.isLoadingReport = false;
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction.');
      this.isLoadingReport = false;
    }
  }

  // Helper to calculate totals for the top table
  get totalSales() {
    return this.reportData.salesBreakdown?.reduce((acc, item) => acc + item.revenue, 0) || 0;
  }

  get totalFees() {
    return this.reportData.salesBreakdown?.reduce((acc, item) => acc + item.fee, 0) || 0;
  }

  get totalStoreOperationFee() {
    const feeItem = this.reportData.expenses.find(e => e.label === LABEL_TOTAL_COMPANY_EXPENSE || e.label === LABEL_TOTAL_COMPANY_EXPENSE_USD);
    return feeItem ? Math.abs(feeItem.amount || 0) : 0;
  }

  get totalNetProfit() {
    const feeItem = this.reportData.expenses.find(e => e.label === LABEL_TOTAL_COMPANY_EXPENSE || e.label === LABEL_TOTAL_COMPANY_EXPENSE_USD);
    const opFee = feeItem ? (feeItem.amount || 0) : 0;
    return this.totalSales + opFee;
  }

  get shopNetProfit() {
    return this.reportData.shopNetProfitUSD || 0;
  }

  get brokerNetProfit() {
    return this.reportData.brokerNetProfitUSD || 0;
  }

  get totalOperatingExpenses() {
    return Math.abs(this.reportData.expenses
      .filter(e => e.category === 'expense' && e.amount !== null && !e.isHeader)
      .reduce((acc, e) => acc + (e.amount || 0), 0));
  }

  get netOperatingIncome() {
    return this.totalSales - this.totalOperatingExpenses;
  }

  get totalFeeCollectionLfcFee() {
    return this.reportData.feeCollectionBreakdown?.reduce((acc, item) => acc + (item.lfcFee || 0), 0) || 0;
  }

  get totalFeeCollectionKcegFee() {
    return this.reportData.feeCollectionBreakdown?.reduce((acc, item) => acc + (item.kcegFee || 0), 0) || 0;
  }

  async exportTotalPLReport() {
    if (!this.selectedPeriod || this.filteredCompanyGridData.length === 0) return;
    this.isExportingTotalPL = true;

    // Open window immediately to avoid popup blockers
    let pdfWindow: Window | null = null;
    pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
    }

    try {
      const aggregatedBreakdownMap = new Map<string, any>();

      let totalOpsAndFees = 0;
      let totalNetProfit = 0;

      for (const row of this.filteredCompanyGridData) {
        const company = row.rawCompany;
        const mappedBrand = this.getBrandName(company.brand || '');
        const currentRates = this.rates.filter((r: any) => {
          const rBrand = this.getBrandName((r as any).brand || '');
          return r.period === this.selectedPeriod && rBrand === mappedBrand;
        });

        const stores = this.cachedStores.filter((s: any) => {
          if (s.isDeleted) return false;
          const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
          return cids.includes(company.id);
        });

        const { breakdown } = this.invoiceService.calculateCompanyRevenueBreakdown(this.cachedAggregations, stores, currentRates, company.id, company.isFeeCollection);
        const exRate = row.exchangeRate || 1;

        breakdown.forEach((item: any) => {
          const key = item.name;
          if (!aggregatedBreakdownMap.has(key)) {
            aggregatedBreakdownMap.set(key, { name: key, revenue: 0, rate: item.rate, fee: 0 });
          }
          const existing = aggregatedBreakdownMap.get(key);
          existing.revenue += (item.revenue * exRate);
          existing.fee += (item.fee * exRate);
        });

        totalOpsAndFees += (row.totalOpsAndFees || 0);
        totalNetProfit += (row.netProfit || 0);
      }

      const salesBreakdown = Array.from(aggregatedBreakdownMap.values());
      const totalSales = salesBreakdown.reduce((sum, i) => sum + i.revenue, 0);
      const totalFees = salesBreakdown.reduce((sum, i) => sum + i.fee, 0);

      this.totalPLData = {
        salesBreakdown,
        totalSales,
        totalFees,
        totalOpsAndFees,
        totalStoreOperationFeeUSD: Math.abs(totalOpsAndFees),
        totalNetProfit
      };

      this.cd.detectChanges();

      // Wait for view to update
      await new Promise(resolve => setTimeout(resolve, 1000));

      const content = document.getElementById('total-pl-pdf-content');
      if (content) {
        const filename = `Total_PL_Report_${this.selectedPeriod.replace('/', '-')}.pdf`;
        await this.pdfService.openPdf(content, filename, 'landscape', pdfWindow || undefined);
      } else {
        if (pdfWindow) pdfWindow.close();
      }

    } catch (error) {
      if (pdfWindow) pdfWindow.close();
      console.error('Error generating Total P/L Report:', error);
      alert('Failed to generate Total P/L Report');
    } finally {
      this.isExportingTotalPL = false;
      this.totalPLData = null;
      this.cd.detectChanges();
    }
  }
}